/**
 * Task keys in commits (commit ↔ task links, slice 1). Pure.
 *
 * A commit mentions `APB-42` when its message has the key as a whole word
 * (case-insensitive; not APB-420, not XAPB-42) or its branch name has it as a
 * segment (`apb-42-login`, `feature/APB-42`, `apb-42`). This is how GitHub,
 * Linear and JetBrains link commits: derived from the text, nothing stored.
 */
import { parseTaskKey } from '../../extensions/bundled/task-manager/taskKeys';

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// A key: a letter, 1–4 more letters/digits, a dash, a number without a leading zero.
// Not preceded by a letter/digit/dash (XAPB-42, a-APB-1) and not followed by a letter/digit (APB-420, APB-42x).
const KEY_IN_TEXT = /(?<![A-Za-z0-9-])([A-Za-z][A-Za-z0-9]{1,4})-([1-9][0-9]{0,8})(?![A-Za-z0-9])/g;

function keyPattern(key) {
  const parsed = parseTaskKey(key);
  if (!parsed) return null;
  return new RegExp(`(?<![A-Za-z0-9-])${escape(parsed.prefix)}-${parsed.number}(?![A-Za-z0-9])`, 'i');
}

/** True when `branch` holds the key as a segment: separated by start/end, `/`, `_` or `-` + a non-digit. */
export function branchMentions(branch, key) {
  const parsed = parseTaskKey(key);
  if (!parsed || !branch) return false;
  const re = new RegExp(`(^|[/_-])${escape(parsed.prefix)}-${parsed.number}($|[/_]|-(?![0-9]))`, 'i');
  // Remote names ("origin/apb-42-x") are fine: "/" is a separator.
  return re.test(String(branch));
}

/** True when the commit's message (summary + body) mentions the key as a whole word. */
export function messageMentions(commit, key) {
  const re = keyPattern(key);
  if (!re) return false;
  return re.test(`${commit?.summary || commit?.message || ''}\n${commit?.body || ''}`);
}

/** How a commit is linked to `key`: 'message', 'branch', or null. */
export function commitMentions(commit, key) {
  if (messageMentions(commit, key)) return 'message';
  if (branchMentions(commit?.branch, key)) return 'branch';
  return null;
}

/**
 * Every key in `text`, upper case, de-duplicated, in order of appearance —
 * only those whose prefix is in `knownPrefixes` when that is given (so "UTF-8"
 * is not a task unless a board is called UTF).
 */
export function extractTaskKeys(text, knownPrefixes = null) {
  const known = knownPrefixes ? new Set([...knownPrefixes].map((p) => String(p).toUpperCase())) : null;
  const out = [];
  const seen = new Set();
  for (const m of String(text || '').matchAll(KEY_IN_TEXT)) {
    const key = `${m[1].toUpperCase()}-${m[2]}`;
    if (known && !known.has(m[1].toUpperCase())) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * The commits of `commits` linked to `key` — or to any of `key`'s list when it
 * is an array (a renamed board: WEB-4 and its old APB-4) — each with
 * `linkedBy`, newest first.
 */
export function commitsForKey(commits, key) {
  const keys = (Array.isArray(key) ? key : [key]).filter(Boolean);
  const out = [];
  for (const c of commits || []) {
    const how = keys.some((k) => messageMentions(c, k)) ? 'message' : keys.some((k) => branchMentions(c?.branch, k)) ? 'branch' : null;
    if (how) out.push({ ...c, linkedBy: how });
  }
  return out.sort((a, b) => (b.time || 0) - (a.time || 0));
}
